import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { validatePassword, validateConfirmPassword, getPasswordRequirementState, SAME_AS_OLD_PASSWORD_MESSAGE, INVALID_CURRENT_PASSWORD_MESSAGE, isInvalidCurrentPasswordMessage, isReusedPasswordMessage } from '../../../utils/validation';
import { toast } from '../../../components/ui/notify';
import { changePassword } from '../../../services/organization/profile/profile.service';

function ChangePassword() {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [passwordVisible, setPasswordVisible] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    letters: false,
    numbers: false,
    special: false
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const canSubmit = Boolean(
    formData.currentPassword.trim() &&
    formData.newPassword &&
    formData.confirmPassword
  );

  const checkPasswordRequirements = (password) => ({
    length: getPasswordRequirementState(password).length,
    letters: getPasswordRequirementState(password).letter,
    numbers: getPasswordRequirementState(password).number,
    special: getPasswordRequirementState(password).special
  });

  const handleChange = (e) => {
    const { id, value } = e.target;
    const newFormData = { ...formData, [id]: value };
    const newErrors = { ...errors, [id]: undefined };

    if (id === 'newPassword') {
      setPasswordRequirements(checkPasswordRequirements(value));
      if (newFormData.confirmPassword) {
        const confirmErrors = validateConfirmPassword(newFormData.confirmPassword, value);
        newErrors.confirmPassword = confirmErrors.length > 0 ? confirmErrors : undefined;
      }
    }

    if (id === 'confirmPassword') {
      const confirmErrors = validateConfirmPassword(value, newFormData.newPassword);
      newErrors.confirmPassword = confirmErrors.length > 0 ? confirmErrors : undefined;
    }

    setFormData(newFormData);
    setErrors(newErrors);
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisible(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || isLoading) return;

    const confirmErrors = validateConfirmPassword(formData.confirmPassword, formData.newPassword);
    if (confirmErrors.length) {
      setErrors({ confirmPassword: confirmErrors });
      return;
    }

    const policyErrors = validatePassword(formData.newPassword);
    if (policyErrors.length) {
      setErrors({ newPassword: policyErrors });
      return;
    }

    setErrors({});
    setIsLoading(true);

    try {
      const response = await changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (response.success) {
        toast.success(response.message || 'Password updated successfully!');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setPasswordRequirements({
          length: false,
          letters: false,
          numbers: false,
          special: false
        });
      } else {
        const message = response.message || 'Please check your current password and try again.';
        if (isInvalidCurrentPasswordMessage(message)) {
          setErrors({ currentPassword: [INVALID_CURRENT_PASSWORD_MESSAGE] });
        } else if (isReusedPasswordMessage(message)) {
          setErrors({ newPassword: [SAME_AS_OLD_PASSWORD_MESSAGE] });
        } else {
          toast.info(message);
        }
      }
    } catch (error) {
      console.error("❌ Error changing password:", error);
      const message = error?.message || "Something went wrong";
      if (isInvalidCurrentPasswordMessage(message)) {
        setErrors({ currentPassword: [INVALID_CURRENT_PASSWORD_MESSAGE] });
      } else if (isReusedPasswordMessage(message)) {
        setErrors({ newPassword: [SAME_AS_OLD_PASSWORD_MESSAGE] });
      } else {
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderInputField = (label, id, value, visible, onChange, error, placeholder, isDisabled = false) => (
    <div className="tw-min-w-[0px] ui-column md:tw-w-full tw-mb-4">
      <div className="profile-field-group">
        <label className="profile-label" htmlFor={id}>{label}</label>
        <div className="profile-field">
          <div className={`tw-relative [&_.ui-input]:tw-pr-[2.75rem] [&.has-error_.ui-input.is-invalid]:tw-pr-[calc(1.5em_+_0.75rem_+_2.25rem)] [&_.profile-password-toggle]:tw-absolute [&_.profile-password-toggle]:tw-top-[50%] [&_.profile-password-toggle]:[transform:translateY(-50%)] [&_.profile-password-toggle]:tw-right-[0.65rem] [&_.profile-password-toggle]:tw-z-[4] [&_.profile-password-toggle]:tw-inline-flex [&_.profile-password-toggle]:tw-items-center [&_.profile-password-toggle]:tw-justify-center [&_.profile-password-toggle]:tw-w-8 [&_.profile-password-toggle]:tw-h-8 [&_.profile-password-toggle]:tw-p-0 [&_.profile-password-toggle]:tw-border-0 [&_.profile-password-toggle]:tw-bg-[transparent] [&_.profile-password-toggle]:tw-text-[#667085] [&_.profile-password-toggle]:tw-cursor-pointer [&_.profile-password-toggle]:tw-leading-[1] [&.has-error_.profile-password-toggle]:tw-right-[calc(0.5rem_+_1.5em_+_0.35rem)] [&_.profile-password-toggle:disabled]:tw-opacity-[0.5] [&_.profile-password-toggle:disabled]:tw-cursor-not-allowed profile-password-input-shell${error ? "   has-error" : ""}`}>
            <input
              type={visible ? "text" : "password"}
              className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input   ${error ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
              id={id}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              disabled={isLoading || isDisabled}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="profile-password-toggle"
              onClick={() => togglePasswordVisibility(id)}
              disabled={isLoading}
              aria-label={visible ? 'Hide password' : 'Show password'}
            >
              <Icon icon={visible ? "mdi:eye-off" : "mdi:eye"} width="18" height="18" />
            </button>
          </div>
          {error && <div className="tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback tw-block">{error[0]}</div>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="tw-bg-[var(--bg-primary)] tw-rounded-[var(--radius-lg)] tw-p-[var(--spacing-lg)] max-[768px]:tw-p-[var(--spacing-sm)] profile-container">
      <div className="profile-card">
        <div className="profile-card-header">
          <h5>Change Password</h5>
        </div>
        <div className="profile-card-body">
          <form onSubmit={handleSubmit}>
            <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
              <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column tw-w-full md:tw-w-1/2">

                {renderInputField(
                  "Current Password",
                  "currentPassword",
                  formData.currentPassword,
                  passwordVisible.currentPassword,
                  handleChange,
                  errors.currentPassword,
                  "Enter current password"
                )}

                {renderInputField(
                  "New Password",
                  "newPassword",
                  formData.newPassword,
                  passwordVisible.newPassword,
                  handleChange,
                  errors.newPassword,
                  "Enter new password",
                  !formData.currentPassword.trim()
                )}

                {/* Password Requirements */}
                <div className="tw-flex tw-flex-wrap tw-gap-[8px] max-[480px]:tw-gap-[6px] password-requirements tw-mt-2">
                  <div className={`tw-inline-flex tw-items-center tw-p-[4px_12px] tw-bg-[#F2F4F7] tw-rounded-[16px] tw-text-[12px] tw-text-[#667085] [transition:all_0.2s_ease] [&.valid]:tw-bg-[#ECFDF3] [&.valid]:tw-text-[#027A48] [&_.icon]:tw-mr-[4px] [&_.icon]:tw-text-[14px] [&.valid_.icon]:tw-text-[#12B76A] [[data-theme='dark']_&]:tw-bg-[#2D3748] [[data-theme='dark']_&]:tw-text-[#A0AEC0] [[data-theme='dark']_&.valid]:tw-bg-[#1C4532] [[data-theme='dark']_&.valid]:tw-text-[#48BB78] max-[480px]:tw-p-[3px_10px] max-[480px]:tw-text-[11px] requirement-badge   ${passwordRequirements.length ? "valid" : ""}`}>
                    <Icon icon={passwordRequirements.length ? "mdi:check" : "mdi:close"} className="icon" /> 8+ Characters
                  </div>
                  <div className={`tw-inline-flex tw-items-center tw-p-[4px_12px] tw-bg-[#F2F4F7] tw-rounded-[16px] tw-text-[12px] tw-text-[#667085] [transition:all_0.2s_ease] [&.valid]:tw-bg-[#ECFDF3] [&.valid]:tw-text-[#027A48] [&_.icon]:tw-mr-[4px] [&_.icon]:tw-text-[14px] [&.valid_.icon]:tw-text-[#12B76A] [[data-theme='dark']_&]:tw-bg-[#2D3748] [[data-theme='dark']_&]:tw-text-[#A0AEC0] [[data-theme='dark']_&.valid]:tw-bg-[#1C4532] [[data-theme='dark']_&.valid]:tw-text-[#48BB78] max-[480px]:tw-p-[3px_10px] max-[480px]:tw-text-[11px] requirement-badge   ${passwordRequirements.letters ? "valid" : ""}`}>
                    <Icon icon={passwordRequirements.letters ? "mdi:check" : "mdi:close"} className="icon" /> Letters
                  </div>
                  <div className={`tw-inline-flex tw-items-center tw-p-[4px_12px] tw-bg-[#F2F4F7] tw-rounded-[16px] tw-text-[12px] tw-text-[#667085] [transition:all_0.2s_ease] [&.valid]:tw-bg-[#ECFDF3] [&.valid]:tw-text-[#027A48] [&_.icon]:tw-mr-[4px] [&_.icon]:tw-text-[14px] [&.valid_.icon]:tw-text-[#12B76A] [[data-theme='dark']_&]:tw-bg-[#2D3748] [[data-theme='dark']_&]:tw-text-[#A0AEC0] [[data-theme='dark']_&.valid]:tw-bg-[#1C4532] [[data-theme='dark']_&.valid]:tw-text-[#48BB78] max-[480px]:tw-p-[3px_10px] max-[480px]:tw-text-[11px] requirement-badge   ${passwordRequirements.numbers ? "valid" : ""}`}>
                    <Icon icon={passwordRequirements.numbers ? "mdi:check" : "mdi:close"} className="icon" /> Numbers
                  </div>
                  <div className={`tw-inline-flex tw-items-center tw-p-[4px_12px] tw-bg-[#F2F4F7] tw-rounded-[16px] tw-text-[12px] tw-text-[#667085] [transition:all_0.2s_ease] [&.valid]:tw-bg-[#ECFDF3] [&.valid]:tw-text-[#027A48] [&_.icon]:tw-mr-[4px] [&_.icon]:tw-text-[14px] [&.valid_.icon]:tw-text-[#12B76A] [[data-theme='dark']_&]:tw-bg-[#2D3748] [[data-theme='dark']_&]:tw-text-[#A0AEC0] [[data-theme='dark']_&.valid]:tw-bg-[#1C4532] [[data-theme='dark']_&.valid]:tw-text-[#48BB78] max-[480px]:tw-p-[3px_10px] max-[480px]:tw-text-[11px] requirement-badge   ${passwordRequirements.special ? "valid" : ""}`}>
                    <Icon icon={passwordRequirements.special ? "mdi:check" : "mdi:close"} className="icon" /> Special Chars
                  </div>
                </div>

                {renderInputField(
                  "Confirm New Password",
                  "confirmPassword",
                  formData.confirmPassword,
                  passwordVisible.confirmPassword,
                  handleChange,
                  errors.confirmPassword,
                  "Confirm new password",
                  !formData.currentPassword.trim()
                )}

                {/* Submit */}
                <div className="tw-min-w-[0px] ui-column tw-w-full tw-mt-4 tw-flex tw-justify-center">
                  <button type="submit" className={`[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary tw-w-1/2   ${isLoading ? "loading" : ""}`} disabled={isLoading || !canSubmit}>
                    {isLoading ? (
                      <>
                        <div className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-2" role="status" />
                        Changing Password...
                      </>
                    ) : 'Change Password'}
                  </button>
                </div>

              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ChangePassword;
